import React, { useMemo } from "react";
import type { CompanyBranchOption, VendorMasterRecord } from "./vendorMasterTypes";

interface Props {
  open: boolean;
  mode: "create" | "edit";
  vendor: VendorMasterRecord;
  branches: CompanyBranchOption[];
  reason: string;
  saving: boolean;
  serverError?: string;
  duplicateNameWarning?: string;
  onChange: (patch: Partial<VendorMasterRecord>) => void;
  onBranchToggle: (branchId: string, checked: boolean) => void;
  onReasonChange: (reason: string) => void;
  onSave: () => void;
  onClose: () => void;
}

const fields: Array<[string, keyof VendorMasterRecord, string]> = [
  ["ERP 업체코드", "vendorCode", "text"],
  ["ERP 사업장코드", "erpSiteCode", "text"],
  ["ERP 관할사업장명", "erpSiteName", "text"],
  ["상호명", "businessName", "text"],
  ["사업자번호", "businessNumber", "text"],
  ["대표자명", "representativeName", "text"],
  ["전화번호", "phone", "tel"],
  ["주소", "address", "text"],
  ["파트", "partName", "text"],
  ["담당영업사원", "salesManagerName", "text"],
  ["관리 메모", "memo", "text"],
];

export default function VendorMasterEditor({
  open,
  mode,
  vendor,
  branches,
  reason,
  saving,
  serverError,
  duplicateNameWarning,
  onChange,
  onBranchToggle,
  onReasonChange,
  onSave,
  onClose,
}: Props) {
  const selectedBranchIds = Array.isArray(vendor.companyBranchIds)
    ? vendor.companyBranchIds
    : vendor.companyBranchId
    ? [vendor.companyBranchId]
    : [];
  const errors = useMemo(() => {
    const next: Record<string, string> = {};
    const code = String(vendor.vendorCode || "").replace(/\D/g, "");
    const businessNumber = String(vendor.businessNumber || "").replace(/\D/g, "");
    const phone = String(vendor.phone || "").replace(/\D/g, "");
    if (!String(vendor.businessName || "").trim()) next.businessName = "상호명을 입력해 주세요.";
    if (String(vendor.businessName || "").trim().length > 100) next.businessName = "상호명은 100자 이하로 입력해 주세요.";
    if (!code && !businessNumber) next.vendorCode = "ERP 업체코드 또는 사업자번호가 필요합니다.";
    if (code && code.length !== 6) next.vendorCode = "업체코드는 6자리 숫자여야 합니다.";
    if (businessNumber && businessNumber.length !== 10) next.businessNumber = "사업자번호는 10자리 숫자여야 합니다.";
    if (phone && ![10, 11].includes(phone.length)) next.phone = "전화번호는 10~11자리 숫자여야 합니다.";
    if (selectedBranchIds.length === 0) next.companyBranchId = "담당 사업장을 선택해 주세요.";
    if (!reason.trim()) next.reason = "저장 사유를 입력해 주세요.";
    return next;
  }, [reason, selectedBranchIds.length, vendor]);

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-slate-950/50 p-3 sm:items-center" role="dialog" aria-modal="true" aria-labelledby="vendor-editor-title">
      <div className="max-h-[92vh] w-full max-w-3xl overflow-y-auto rounded-3xl bg-white p-5 shadow-2xl">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-xs font-black uppercase tracking-[0.18em] text-emerald-600">Vendor Master</p>
            <h3 id="vendor-editor-title" className="mt-1 text-xl font-black text-slate-950">
              {mode === "edit" ? "거래처 수정" : "신규 거래처 등록"}
            </h3>
            <p className="mt-2 text-sm font-bold leading-6 text-slate-500">기존 vendorId와 계약·결제 연결은 변경하지 않습니다.</p>
          </div>
          <button type="button" onClick={onClose} disabled={saving} className="shrink-0 whitespace-nowrap rounded-full bg-slate-100 px-3 py-2 text-sm font-black text-slate-600 disabled:opacity-50">닫기</button>
        </div>

        <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
          {fields.map(([label, field, type]) => (
            <label key={String(field)} className="block">
              <span className="mb-1 block text-sm font-black text-slate-700">{label}</span>
              <input
                type={type}
                value={String(vendor[field] || "")}
                onChange={(event) => onChange({ [field]: event.target.value })}
                aria-invalid={Boolean(errors[String(field)])}
                className="min-h-[48px] w-full rounded-xl border border-slate-200 px-3 text-base font-bold outline-none focus:border-emerald-500"
              />
              {errors[String(field)] && <span className="mt-1 block text-xs font-bold text-red-600">{errors[String(field)]}</span>}
            </label>
          ))}
          <label className="block">
            <span className="mb-1 block text-sm font-black text-slate-700">관리상태</span>
            <select value={String(vendor.status || "ACTIVE")} onChange={(event) => onChange({ status: event.target.value })} className="min-h-[48px] w-full rounded-xl border border-slate-200 px-3 text-base font-bold outline-none focus:border-emerald-500">
              <option value="ACTIVE">거래중</option>
              <option value="INACTIVE">거래중지</option>
              <option value="WATCH">주의</option>
              <option value="LEGAL">법적관리</option>
            </select>
          </label>
          <div>
            <span className="mb-1 block text-sm font-black text-slate-700">담당 사업장</span>
            <div className="grid grid-cols-1 gap-2 rounded-2xl border border-slate-200 p-3">
              {branches.filter((branch) => branch.active !== false).map((branch) => {
                const branchId = String(branch.branchId || branch.id || "");
                return <label key={branchId} className="flex items-center gap-3 text-sm font-black text-slate-700"><input type="checkbox" checked={selectedBranchIds.includes(branchId)} onChange={(event) => onBranchToggle(branchId, event.target.checked)} />{branch.branchName}</label>;
              })}
            </div>
            {errors.companyBranchId && <span className="mt-1 block text-xs font-bold text-red-600">{errors.companyBranchId}</span>}
          </div>
          <label className="block">
            <span className="mb-1 block text-sm font-black text-slate-700">저장 사유</span>
            <input value={reason} onChange={(event) => onReasonChange(event.target.value)} placeholder="신규 등록 또는 수정 사유" className="min-h-[48px] w-full rounded-xl border border-slate-200 px-3 text-base font-bold outline-none focus:border-emerald-500" />
            {errors.reason && <span className="mt-1 block text-xs font-bold text-red-600">{errors.reason}</span>}
          </label>
        </div>

        <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2">
          {[['allowLinkPayment', '문자링크결제 허용'], ['allowKeyinPayment', '키인결제 허용']].map(([field, label]) => (
            <label key={field} className="flex items-center gap-3 rounded-2xl border border-slate-200 p-3 text-sm font-black text-slate-700">
              <input type="checkbox" checked={vendor.paymentProfile?.[field] === true} onChange={(event) => onChange({ paymentProfile: { ...(vendor.paymentProfile || {}), [field]: event.target.checked } })} />{label}
            </label>
          ))}
        </div>
        {duplicateNameWarning && (
          <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-bold leading-6 text-amber-800">
            {duplicateNameWarning}
          </div>
        )}
        {serverError && <div className="mt-4 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-bold leading-6 text-red-700">{serverError}</div>}
        <div className="mt-5 flex flex-col gap-2 sm:flex-row sm:justify-end">
          <button type="button" onClick={onClose} disabled={saving} className="min-h-[48px] rounded-2xl border border-slate-200 px-4 py-3 text-base font-black text-slate-600 disabled:opacity-50">취소</button>
          <button type="button" onClick={onSave} disabled={saving || Object.keys(errors).length > 0} className="min-h-[48px] rounded-2xl bg-emerald-700 px-4 py-3 text-base font-black text-white disabled:bg-slate-300">{saving ? "저장 중..." : "저장"}</button>
        </div>
      </div>
    </div>
  );
}
